import type { LucideIcon } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export interface QuickAction {
  key: string;
  label: string;
  icon: LucideIcon;
  to?: string;
  href?: string;
  onClick?: () => void;
  variant?: "default" | "outline" | "ghost" | "secondary" | "destructive";
  size?: "default" | "sm";
  disabled?: boolean;
  /** Anima el ícono con animate-spin (ej. acción en curso). */
  spinning?: boolean;
  dataTour?: string;
  /** Texto explicando el impacto de la acción (ej. "El cliente recibe un email de cancelación"). Se muestra en un tooltip al hacer hover. */
  tooltip?: string;
}

export interface QuickActionsProps {
  actions: QuickAction[];
  className?: string;
}

/** Fila de acciones rápidas del Design System (F2) — reemplaza los botones ad-hoc del Dashboard. */
export function QuickActions({ actions, className }: QuickActionsProps) {
  return (
    <TooltipProvider delayDuration={200}>
      <div className={cn("flex flex-wrap gap-2", className)}>
        {actions.map((action) => {
          const content = (
            <>
              <action.icon className={cn("h-4 w-4", action.spinning && "animate-spin")} aria-hidden />
              {action.label}
            </>
          );
          const buttonProps = {
            variant: action.variant ?? "default",
            size: action.size,
            className: "gap-2",
            "data-tour": action.dataTour,
          } as const;

          const button = action.to ? (
            <Button key={action.key} {...buttonProps} disabled={action.disabled} asChild>
              <Link to={action.to}>{content}</Link>
            </Button>
          ) : action.href ? (
            <Button key={action.key} {...buttonProps} disabled={action.disabled} asChild>
              <a href={action.href} target="_blank" rel="noopener noreferrer">
                {content}
              </a>
            </Button>
          ) : (
            <Button key={action.key} {...buttonProps} disabled={action.disabled} onClick={action.onClick} type="button">
              {content}
            </Button>
          );

          if (!action.tooltip) return button;
          return (
            <Tooltip key={action.key}>
              <TooltipTrigger asChild>{button}</TooltipTrigger>
              <TooltipContent>{action.tooltip}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
